import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  canManageModuleToggles,
  canManageOrganizationAccess,
  getAdminUser,
  getAdminUsers,
  replaceAdminUserFacilities,
  updateAdminUserRole,
} from '../../lib/services/adminUsers'
import {
  createFacility,
  type FacilityFeatureFlag,
  getFacilities,
  getFacilityFeatureFlags,
  getOrganizationProfile,
  updateFacility,
  updateFacilityFeatureFlag,
  updateOrganizationProfile,
  type FacilityInput,
} from '../../lib/services/facilities'
import {
  createCheckoutSession,
  getModuleBillingState,
  mutateModuleBilling,
  type BillingInterval,
  type BillingTier,
  type ModuleBillingKey,
  type ModuleBillingState,
} from '../../lib/services/billing'
import {
  DAILY_OPERATIONAL_CONTROL_FEATURE_PREFIX,
  DAILY_OPERATIONAL_CONTROLS,
} from '../../lib/services/dailyAssessment'
import { createSiteVisit } from '../../lib/services/visits'

const ROLE_OPTIONS = ['admin', 'editor', 'user'] as const

type OrganizationAssignableRole = (typeof ROLE_OPTIONS)[number]
const MODULE_KEYS = ['qapi', 'fire_safety', 'acquisition', 'notifications', 'compliance_charts', 'corporate_actions', 'branded_exports', 'training_recommendations', 'insurance_verification', 'patient_comms', 'pat_automation'] as const
const BILLING_TIERS: ReadonlyArray<{ tier: BillingTier; title: string; description: string }> = [
  { tier: 'standalone', title: 'Standalone', description: 'Single-facility ASC operators and DON-led teams.' },
  { tier: 'consultant', title: 'Consultant', description: 'Independent consultants managing multiple ASC clients.' },
  { tier: 'enterprise', title: 'Enterprise', description: 'Corporate or portfolio compliance teams.' },
]

type PendingModuleAction = {
  moduleKey: ModuleBillingKey
  enabled: boolean
  label: string
}

function emptyFacilityForm(): FacilityInput {
  return {
    facility_name: '',
    state: '',
    facility_type: 'ASC',
    address: '',
    city: '',
    zip_code: '',
    license_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    employee_count: null,
    specialties: '',
    notes: '',
  }
}

function formatUserLabel(user: { first_name: string | null; last_name: string | null; email: string; role: string }): string {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
  return `${fullName || user.email} (${user.role})`
}

function dailyControlFeatureKey(controlKey: string): string {
  return `${DAILY_OPERATIONAL_CONTROL_FEATURE_PREFIX}${controlKey}`
}

export function OrganizationAccessPage() {
  const { viewerContext } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState<OrganizationAssignableRole>('user')
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)
  const [organizationName, setOrganizationName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateValue, setStateValue] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [employeeCount, setEmployeeCount] = useState('')
  const [notes, setNotes] = useState('')
  const [editingFacilityId, setEditingFacilityId] = useState('')
  const [facilityForm, setFacilityForm] = useState<FacilityInput>(emptyFacilityForm())
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [visitFacilityId, setVisitFacilityId] = useState('')
  const [visitSurveyorId, setVisitSurveyorId] = useState('')
  const [visitType, setVisitType] = useState<'operations' | 'accreditation'>('operations')
  const [visitDate, setVisitDate] = useState('')
  const [visitError, setVisitError] = useState<string | null>(null)
  const [visitSuccess, setVisitSuccess] = useState<string | null>(null)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [billingSuccess, setBillingSuccess] = useState<string | null>(null)
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  const [autoCheckoutHandled, setAutoCheckoutHandled] = useState(false)
  const [moduleBillingError, setModuleBillingError] = useState<string | null>(null)
  const [moduleBillingSuccess, setModuleBillingSuccess] = useState<string | null>(null)
  const [pendingModuleAction, setPendingModuleAction] = useState<PendingModuleAction | null>(null)
  const [modulePreviews, setModulePreviews] = useState<Partial<Record<ModuleBillingKey, ModuleBillingState>>>({})
  const queryClient = useQueryClient()

  const canManage = canManageOrganizationAccess(viewerContext)
  const canManageToggles = canManageModuleToggles(viewerContext)

  const usersQuery = useQuery({
    queryKey: ['org-access-users'],
    queryFn: getAdminUsers,
    enabled: canManage,
  })

  const facilitiesQuery = useQuery({
    queryKey: ['org-access-facilities'],
    queryFn: getFacilities,
    enabled: canManage,
  })

  const detailQuery = useQuery({
    queryKey: ['org-access-user-detail', selectedUserId],
    queryFn: () => getAdminUser(selectedUserId),
    enabled: canManage && !!selectedUserId,
  })

  const organizationProfileQuery = useQuery({
    queryKey: ['organization-profile'],
    queryFn: getOrganizationProfile,
    enabled: canManage,
  })

  const organizationProfileMutation = useMutation({
    mutationFn: updateOrganizationProfile,
    onSuccess: async () => {
      await organizationProfileQuery.refetch()
    },
  })
  const saveFacilityMutation = useMutation({
    mutationFn: async () => {
      if (editingFacilityId) {
        return await updateFacility(editingFacilityId, facilityForm)
      }
      return await createFacility(facilityForm)
    },
    onSuccess: async () => {
      await Promise.all([facilitiesQuery.refetch(), queryClient.invalidateQueries({ queryKey: ['assessment-facilities'] })])
    },
  })
  const featureFlagsQuery = useQuery<FacilityFeatureFlag[]>({
    queryKey: ['org-access-feature-flags'],
    queryFn: () => getFacilityFeatureFlags(),
    enabled: canManage,
  })
  const updateFeatureFlagMutation = useMutation({
    mutationFn: async (payload: { facilityId: string; featureKey: string; enabled: boolean }) =>
      await updateFacilityFeatureFlag(payload.facilityId, payload.featureKey, payload.enabled),
    onSuccess: async () => {
      await featureFlagsQuery.refetch()
    },
  })
  const createVisitMutation = useMutation({
    mutationFn: async () =>
      await createSiteVisit({
        facility_id: visitFacilityId,
        visit_type: visitType,
        assigned_surveyor_id: visitSurveyorId,
        survey_date: visitDate || undefined,
      }),
  })
  const checkoutMutation = useMutation({
    mutationFn: async (payload: { tier: BillingTier; billing_interval: BillingInterval }) =>
      await createCheckoutSession(payload),
  })
  const moduleBillingQuery = useQuery({
    queryKey: ['module-billing'],
    queryFn: getModuleBillingState,
    enabled: canManage,
  })
  const moduleBillingMutation = useMutation({
    mutationFn: async (payload: { moduleKey: ModuleBillingKey; enabled: boolean; previewOnly: boolean }) =>
      await mutateModuleBilling(payload.moduleKey, payload.enabled, payload.previewOnly),
    onSuccess: async (response) => {
      setModulePreviews((current) => ({
        ...current,
        [response.module.module_key]: response.module,
      }))
      await moduleBillingQuery.refetch()
    },
  })

  const users = (usersQuery.data || []).filter((user) => user.active && user.role !== 'superadmin')
  const facilities = useMemo(() => facilitiesQuery.data ?? [], [facilitiesQuery.data])
  const featureFlags = featureFlagsQuery.data || []

  useEffect(() => {
    if (!detailQuery.data) return
    const role = detailQuery.data.user.role
    setSelectedRole(role === 'admin' ? 'admin' : role === 'editor' ? 'editor' : 'user')
    setSelectedFacilityIds(detailQuery.data.facility_assignments.map((row) => row.facility_id))
  }, [detailQuery.data])

  useEffect(() => {
    if (!organizationProfileQuery.data) return
    setOrganizationName(organizationProfileQuery.data.name)
    setContactName(organizationProfileQuery.data.contact_name ?? '')
    setContactEmail(organizationProfileQuery.data.contact_email ?? '')
    setContactPhone(organizationProfileQuery.data.contact_phone ?? '')
    setAddress(organizationProfileQuery.data.address ?? '')
    setCity(organizationProfileQuery.data.city ?? '')
    setStateValue(organizationProfileQuery.data.state ?? '')
    setZipCode(organizationProfileQuery.data.zip_code ?? '')
    setEmployeeCount(
      organizationProfileQuery.data.employee_count === null ? '' : String(organizationProfileQuery.data.employee_count),
    )
    setNotes(organizationProfileQuery.data.notes ?? '')
  }, [organizationProfileQuery.data])

  useEffect(() => {
    if (!editingFacilityId) {
      setFacilityForm(emptyFacilityForm())
      return
    }
    const facility = facilities.find((entry) => entry.facility_id === editingFacilityId)
    if (!facility) return
    setFacilityForm({
      facility_name: facility.facility_name,
      state: facility.state ?? '',
      facility_type: facility.facility_type ?? 'ASC',
      address: facility.address ?? '',
      city: facility.city ?? '',
      zip_code: facility.zip_code ?? '',
      license_number: facility.license_number ?? '',
      contact_name: facility.contact_name ?? '',
      contact_email: facility.contact_email ?? '',
      contact_phone: facility.contact_phone ?? '',
      employee_count: facility.employee_count,
      specialties: facility.specialties.join(', '),
      notes: facility.notes ?? '',
    })
  }, [editingFacilityId, facilities])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const requestedTier = params.get('checkout_tier')
    const requestedInterval = params.get('billing_interval')
    if (!requestedInterval) return
    if (requestedInterval === 'annual' || requestedInterval === 'monthly') {
      setBillingInterval(requestedInterval)
    }
    if (!requestedTier || !canManage || autoCheckoutHandled || checkoutMutation.isPending) return
    if (!BILLING_TIERS.some((entry) => entry.tier === requestedTier)) return
    setAutoCheckoutHandled(true)
    navigate(location.pathname, { replace: true })
    setBillingError(null)
    setBillingSuccess(`Preparing Stripe checkout for the ${requestedTier} plan...`)
    void checkoutMutation
      .mutateAsync({ tier: requestedTier as BillingTier, billing_interval: requestedInterval === 'annual' ? 'annual' : 'monthly' })
      .then((session) => {
        window.location.assign(session.url)
      })
      .catch((error: Error) => {
        setBillingSuccess(null)
        setBillingError(error.message)
      })
  }, [autoCheckoutHandled, canManage, checkoutMutation, location.pathname, location.search, navigate])

  async function launchStripeCheckout(tier: BillingTier) {
    setBillingError(null)
    setBillingSuccess(`Redirecting ${organizationName || 'your organization'} to Stripe checkout...`)
    try {
      const session = await checkoutMutation.mutateAsync({
        tier,
        billing_interval: billingInterval,
      })
      window.location.assign(session.url)
    } catch (error) {
      setBillingSuccess(null)
      setBillingError((error as Error).message)
    }
  }

  function formatCurrency(cents: number | null, currency: string | null) {
    if (cents === null || !currency) return 'No preview available'
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100)
  }

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Organization access management is restricted to Superadmin role.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Organization Access Management</h1>
        <p className="mt-1 text-sm text-slate-600">Assign organization roles and facility scope for tenant users.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Stripe Billing</h2>
        <p className="mt-1 text-sm text-slate-600">
          Launch a tenant-scoped Stripe checkout for the selected subscription tier. Billing portal and module-level billing land in later sprints.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-slate-700" htmlFor="billing-interval">
            Billing interval
          </label>
          <select
            id="billing-interval"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={billingInterval}
            onChange={(event) => setBillingInterval(event.target.value as BillingInterval)}
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Current tier: {viewerContext?.tier ?? 'standalone'}
          </span>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {BILLING_TIERS.map((plan) => (
            <div key={plan.tier} className="v11-dashboard-card p-4">
              <p className="font-brand text-lg font-bold text-[var(--brand-deep)]">{plan.title}</p>
              <p className="mt-2 text-sm text-slate-600">{plan.description}</p>
              <button
                className="btn-primary mt-4 w-full text-sm"
                disabled={checkoutMutation.isPending}
                onClick={() => void launchStripeCheckout(plan.tier)}
              >
                {checkoutMutation.isPending ? 'Redirecting...' : `Start ${plan.title} Checkout`}
              </button>
            </div>
          ))}
        </div>
        {billingError && <p className="mt-3 text-sm text-red-600">{billingError}</p>}
        {billingSuccess && <p className="mt-3 text-sm text-emerald-700">{billingSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Module Billing</h2>
        <p className="mt-1 text-sm text-slate-600">
          Preview and apply tenant-wide module add-ons. Sprint 2 derives billed quantity from your current facility count and then synchronizes paid entitlements into facility feature flags.
        </p>
        {moduleBillingQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading module billing...</p>}
        {moduleBillingQuery.error && <p className="mt-2 text-sm text-red-600">{(moduleBillingQuery.error as Error).message}</p>}
        {moduleBillingQuery.data && (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span>Subscription status: {moduleBillingQuery.data.subscription_status}</span>
              <span>Interval: {moduleBillingQuery.data.billing_interval}</span>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {moduleBillingQuery.data.modules.map((module) => {
                const preview = modulePreviews[module.module_key]?.preview ?? module.preview
                const hasActiveEntitlement = module.has_active_subscription_item || module.entitlement_status === 'active'
                const nextEnabled = !hasActiveEntitlement
                return (
                  <div key={module.module_key} className="v11-dashboard-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-brand text-lg font-bold text-[var(--brand-deep)]">{module.title}</p>
                        <p className="mt-1 text-sm text-slate-600">{module.description}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        {module.entitlement_status}
                      </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-600">
                      <div>
                        <dt className="font-semibold text-slate-800">Facilities</dt>
                        <dd>{module.facility_count}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Billed Qty</dt>
                        <dd>{module.billed_quantity}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Enabled Facilities</dt>
                        <dd>{module.enabled_facility_count}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Next Action</dt>
                        <dd>{nextEnabled ? 'Enable add-on' : 'Remove add-on'}</dd>
                      </div>
                    </dl>
                    <p className="mt-3 text-sm text-slate-600">
                      Preview amount:{' '}
                      <span className="font-semibold text-slate-800">
                        {formatCurrency(preview?.total_delta_cents ?? null, preview?.currency ?? null)}
                      </span>
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
                        disabled={moduleBillingMutation.isPending}
                        onClick={async () => {
                          setModuleBillingError(null)
                          setModuleBillingSuccess(null)
                          setPendingModuleAction({
                            moduleKey: module.module_key,
                            enabled: nextEnabled,
                            label: nextEnabled ? `Enable ${module.title}` : `Remove ${module.title}`,
                          })
                          try {
                            await moduleBillingMutation.mutateAsync({
                              moduleKey: module.module_key,
                              enabled: nextEnabled,
                              previewOnly: true,
                            })
                            setModuleBillingSuccess(`Preview ready for ${module.title}. Review the amount before applying.`)
                          } catch (error) {
                            setModuleBillingError((error as Error).message)
                          }
                        }}
                      >
                        Preview {nextEnabled ? 'Enable' : 'Removal'}
                      </button>
                      <button
                        className="btn-primary text-sm"
                        disabled={
                          moduleBillingMutation.isPending ||
                          pendingModuleAction?.moduleKey !== module.module_key ||
                          pendingModuleAction?.enabled !== nextEnabled
                        }
                        onClick={async () => {
                          setModuleBillingError(null)
                          setModuleBillingSuccess(null)
                          try {
                            await moduleBillingMutation.mutateAsync({
                              moduleKey: module.module_key,
                              enabled: nextEnabled,
                              previewOnly: false,
                            })
                            setModuleBillingSuccess(`${module.title} billing updated and feature entitlements synchronized.`)
                            setPendingModuleAction(null)
                          } catch (error) {
                            setModuleBillingError((error as Error).message)
                          }
                        }}
                      >
                        {moduleBillingMutation.isPending && pendingModuleAction?.moduleKey === module.module_key
                          ? 'Applying...'
                          : nextEnabled
                            ? 'Apply Enable'
                            : 'Apply Removal'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
        {moduleBillingError && <p className="mt-3 text-sm text-red-600">{moduleBillingError}</p>}
        {moduleBillingSuccess && <p className="mt-3 text-sm text-emerald-700">{moduleBillingSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Organization Profile</h2>
        <p className="mt-1 text-sm text-slate-600">Manage tenant profile metadata used across onboarding and visits.</p>
        {organizationProfileQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading organization profile...</p>}
        {organizationProfileQuery.error && (
          <p className="mt-2 text-sm text-red-600">{(organizationProfileQuery.error as Error).message}</p>
        )}
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Organization name"
            value={organizationName}
            onChange={(event) => setOrganizationName(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact name"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact email"
            value={contactEmail}
            onChange={(event) => setContactEmail(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact phone"
            value={contactPhone}
            onChange={(event) => setContactPhone(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Address"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="City"
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="State"
            value={stateValue}
            onChange={(event) => setStateValue(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Zip code"
            value={zipCode}
            onChange={(event) => setZipCode(event.target.value)}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Employee count"
            value={employeeCount}
            onChange={(event) => setEmployeeCount(event.target.value)}
          />
          <textarea
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
            rows={3}
            placeholder="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
        <button
          className="btn-primary mt-3 text-sm"
          disabled={organizationProfileMutation.isPending}
          onClick={async () => {
            setActionError(null)
            setActionSuccess(null)
            try {
              await organizationProfileMutation.mutateAsync({
                name: organizationName,
                contact_name: contactName,
                contact_email: contactEmail,
                contact_phone: contactPhone,
                address,
                city,
                state: stateValue,
                zip_code: zipCode,
                employee_count: employeeCount.trim() === '' ? null : Number.parseInt(employeeCount, 10),
                notes,
              })
              setActionSuccess('Organization profile updated.')
            } catch (err) {
              setActionError((err as Error).message)
            }
          }}
        >
          {organizationProfileMutation.isPending ? 'Saving...' : 'Save Organization Profile'}
        </button>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Facility Profile</h2>
        <p className="mt-1 text-sm text-slate-600">Create or update facility metadata and contact information.</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={editingFacilityId}
            onChange={(event) => setEditingFacilityId(event.target.value)}
          >
            <option value="">Create new facility</option>
            {facilities.map((facility) => (
              <option key={facility.facility_id} value={facility.facility_id}>
                {facility.facility_name}
              </option>
            ))}
          </select>
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Facility name"
            value={facilityForm.facility_name}
            onChange={(event) => setFacilityForm((current) => ({ ...current, facility_name: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="State (CA)"
            value={facilityForm.state}
            onChange={(event) => setFacilityForm((current) => ({ ...current, state: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Facility type"
            value={facilityForm.facility_type ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, facility_type: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Address"
            value={facilityForm.address ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, address: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="City"
            value={facilityForm.city ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, city: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Zip"
            value={facilityForm.zip_code ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, zip_code: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="License number"
            value={facilityForm.license_number ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, license_number: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact name"
            value={facilityForm.contact_name ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, contact_name: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact email"
            value={facilityForm.contact_email ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, contact_email: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Contact phone"
            value={facilityForm.contact_phone ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, contact_phone: event.target.value }))}
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Employee count"
            value={facilityForm.employee_count ?? ''}
            onChange={(event) =>
              setFacilityForm((current) => ({
                ...current,
                employee_count: event.target.value === '' ? null : Number.parseInt(event.target.value, 10),
              }))
            }
          />
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
            placeholder="Specialties (comma separated)"
            value={typeof facilityForm.specialties === 'string' ? facilityForm.specialties : ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, specialties: event.target.value }))}
          />
          <textarea
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
            rows={3}
            placeholder="Notes"
            value={facilityForm.notes ?? ''}
            onChange={(event) => setFacilityForm((current) => ({ ...current, notes: event.target.value }))}
          />
        </div>
        <div className="mt-3 flex gap-2">
          <button
            className="btn-primary text-sm"
            disabled={saveFacilityMutation.isPending || !facilityForm.facility_name || !facilityForm.state}
            onClick={async () => {
              setProfileError(null)
              setProfileSuccess(null)
              try {
                await saveFacilityMutation.mutateAsync()
                setProfileSuccess(editingFacilityId ? 'Facility profile updated.' : 'Facility created.')
                if (!editingFacilityId) {
                  setFacilityForm(emptyFacilityForm())
                }
              } catch (err) {
                setProfileError((err as Error).message)
              }
            }}
          >
            {saveFacilityMutation.isPending ? 'Saving...' : editingFacilityId ? 'Update Facility' : 'Create Facility'}
          </button>
          {editingFacilityId && (
            <button
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={() => {
                setEditingFacilityId('')
                setFacilityForm(emptyFacilityForm())
              }}
            >
              Clear
            </button>
          )}
        </div>
        {profileError && <p className="mt-2 text-sm text-red-600">{profileError}</p>}
        {profileSuccess && <p className="mt-2 text-sm text-emerald-700">{profileSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Module Controls</h2>
        <p className="mt-1 text-sm text-slate-600">Facility module toggles are restricted to backend-superadmin authority.</p>
        {!canManageToggles && (
          <p className="mt-2 text-sm text-amber-700">Toggle actions are disabled for non-backend-superadmin users.</p>
        )}
        <div className="mt-3 grid gap-3">
          {facilities.map((facility) => (
            <div key={facility.facility_id} className="v11-dashboard-card p-3">
              <p className="text-sm font-semibold text-slate-800">{facility.facility_name}</p>
              <div className="mt-2 grid gap-2 md:grid-cols-3">
                {MODULE_KEYS.map((moduleKey) => {
                  const row = featureFlags.find(
                    (flag) => flag.facility_id === facility.facility_id && flag.feature_key === moduleKey,
                  )
                  return (
                    <label key={`${facility.facility_id}-${moduleKey}`} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        disabled={!canManageToggles || updateFeatureFlagMutation.isPending}
                        checked={row?.enabled === true}
                        onChange={async (event) => {
                          try {
                            await updateFeatureFlagMutation.mutateAsync({
                              facilityId: facility.facility_id,
                              featureKey: moduleKey,
                              enabled: event.target.checked,
                            })
                          } catch {
                            // handled by mutation state and backend errors surfaced via network layer
                          }
                        }}
                      />
                      <span>{moduleKey.replace('_', ' ')}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        {featureFlagsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading module flags...</p>}
        {featureFlagsQuery.error && <p className="mt-2 text-sm text-red-600">{(featureFlagsQuery.error as Error).message}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Daily Assessment Controls</h2>
        <p className="mt-1 text-sm text-slate-600">
          Configure which operational checks appear in each facility's daily assessment. Controls are on by default unless disabled here.
        </p>
        {!canManageToggles && (
          <p className="mt-2 text-sm text-amber-700">Daily control actions are disabled for non-backend-superadmin users.</p>
        )}
        <div className="mt-3 grid gap-3">
          {facilities.map((facility) => (
            <div key={`${facility.facility_id}-daily-controls`} className="rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-semibold text-slate-800">{facility.facility_name}</p>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {DAILY_OPERATIONAL_CONTROLS.map((control) => {
                  const featureKey = dailyControlFeatureKey(control.key)
                  const row = featureFlags.find(
                    (flag) => flag.facility_id === facility.facility_id && flag.feature_key === featureKey,
                  )
                  return (
                    <label key={`${facility.facility_id}-${featureKey}`} className="flex items-start gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        disabled={!canManageToggles || updateFeatureFlagMutation.isPending}
                        checked={row?.enabled !== false}
                        onChange={async (event) => {
                          try {
                            await updateFeatureFlagMutation.mutateAsync({
                              facilityId: facility.facility_id,
                              featureKey,
                              enabled: event.target.checked,
                            })
                          } catch {
                            // handled by mutation state and backend errors surfaced via network layer
                          }
                        }}
                      />
                      <span>{control.label}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        {featureFlagsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading daily assessment controls...</p>}
        {featureFlagsQuery.error && <p className="mt-2 text-sm text-red-600">{(featureFlagsQuery.error as Error).message}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Standard Site Visit Intake</h2>
        <p className="mt-1 text-sm text-slate-600">Create operations/accreditation visits from admin workflow.</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={visitFacilityId}
            onChange={(event) => setVisitFacilityId(event.target.value)}
          >
            <option value="">Select facility</option>
            {facilities.map((facility) => (
              <option key={facility.facility_id} value={facility.facility_id}>
                {facility.facility_name}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={visitSurveyorId}
            onChange={(event) => setVisitSurveyorId(event.target.value)}
          >
            <option value="">Select assigned user</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {formatUserLabel(user)}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={visitType}
            onChange={(event) => setVisitType(event.target.value as 'operations' | 'accreditation')}
          >
            <option value="operations">operations</option>
            <option value="accreditation">accreditation</option>
          </select>
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            type="date"
            value={visitDate}
            onChange={(event) => setVisitDate(event.target.value)}
          />
        </div>
        <button
          className="btn-primary mt-3 text-sm"
          disabled={createVisitMutation.isPending || !visitFacilityId || !visitSurveyorId}
          onClick={async () => {
            setVisitError(null)
            setVisitSuccess(null)
            try {
              await createVisitMutation.mutateAsync()
              setVisitSuccess('Site visit created.')
              setVisitDate('')
            } catch (err) {
              setVisitError((err as Error).message)
            }
          }}
        >
          {createVisitMutation.isPending ? 'Creating...' : 'Create Site Visit'}
        </button>
        {visitError && <p className="mt-2 text-sm text-red-600">{visitError}</p>}
        {visitSuccess && <p className="mt-2 text-sm text-emerald-700">{visitSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Select User</h2>
        {usersQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading users...</p>}
        {usersQuery.error && <p className="mt-2 text-sm text-red-600">{(usersQuery.error as Error).message}</p>}
        {!usersQuery.isLoading && !usersQuery.error && users.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No organization users are available.</p>
        )}
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedUserId}
            onChange={(event) => setSelectedUserId(event.target.value)}
            disabled={users.length === 0}
          >
            <option value="">Choose user</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {formatUserLabel(user)}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedRole}
            disabled={!selectedUserId}
            onChange={(event) => setSelectedRole(event.target.value as OrganizationAssignableRole)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Facility Scope</h2>
        {facilitiesQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading facilities...</p>}
        {facilitiesQuery.error && <p className="mt-2 text-sm text-red-600">{(facilitiesQuery.error as Error).message}</p>}
        {!facilitiesQuery.isLoading && !facilitiesQuery.error && facilities.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No facilities are available for assignment.</p>
        )}
        <div className="mt-3 grid gap-2">
          {facilities.map((facility) => (
            <label
              key={facility.facility_id}
              className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <input
                type="checkbox"
                disabled={!selectedUserId}
                checked={selectedFacilityIds.includes(facility.facility_id)}
                onChange={() =>
                  setSelectedFacilityIds((current) =>
                    current.includes(facility.facility_id)
                      ? current.filter((id) => id !== facility.facility_id)
                      : [...current, facility.facility_id],
                  )
                }
              />
              <span>{facility.facility_name}</span>
            </label>
          ))}
        </div>
        <button
          className="btn-primary mt-3 text-sm"
          disabled={isActing || !selectedUserId}
          onClick={async () => {
            setActionError(null)
            setActionSuccess(null)
            setIsActing(true)
            try {
              await updateAdminUserRole(selectedUserId, selectedRole)
              await replaceAdminUserFacilities(selectedUserId, selectedFacilityIds)
              setActionSuccess('Organization access updated.')
              await Promise.all([usersQuery.refetch(), detailQuery.refetch()])
            } catch (err) {
              setActionError((err as Error).message)
            } finally {
              setIsActing(false)
            }
          }}
        >
          Save Access
        </button>
        {detailQuery.error && <p className="mt-2 text-sm text-red-600">{(detailQuery.error as Error).message}</p>}
        {actionError && <p className="mt-2 text-sm text-red-600">{actionError}</p>}
        {actionSuccess && <p className="mt-2 text-sm text-emerald-700">{actionSuccess}</p>}
      </section>
    </div>
  )
}
